import type { Thing, WithContext } from 'schema-dts';

interface Props<T extends Thing> {
  data: T | WithContext<T>;
}

// Characters that can break out of a <script> context or are unsafe in inline scripts.
// Escaping them as JSON unicode escapes keeps the JSON semantically identical (parsers
// decode the escapes back to the original characters) while preventing a malicious value
// such as "</script>" from terminating the JSON-LD block and injecting markup (XSS).
const HTML_ESCAPES: Record<string, string> = {
  '<': '\\u003c',
  '>': '\\u003e',
  '&': '\\u0026',
  '\u2028': '\\u2028',
  '\u2029': '\\u2029'
};

const escapeJsonForHtml = (json: string) => json.replace(/[<>&\u2028\u2029]/g, char => HTML_ESCAPES[char]);

const JsonLdScript = <T extends Thing>({ data }: Props<T>) => {
  const linkedData: WithContext<T> = { '@context': 'https://schema.org', ...(data as any) };

  const stringifiedData = escapeJsonForHtml(JSON.stringify(linkedData));

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: stringifiedData }} />;
};

export default JsonLdScript;
